import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBolt,
  FaCheckCircle,
  FaClipboardList,
  FaCode,
  FaCogs,
  FaDoorOpen,
  FaLaptopCode,
  FaMedal,
  FaPalette,
  FaRegChartBar,
  FaRocket,
  FaServer,
  FaThLarge,
  FaUsers,
} from "react-icons/fa";
import Header from "./Header";
import Footer from "./Footer";
import "./HomePage.css";
import useScrollReveal from "./useScrollReveal";

import image1 from "./Images/gbtP.jpg";
import image2 from "./Images/gbtP1.jpg";
import image3 from "./Images/gbtP2.jpg";
import image4 from "./Images/gbtP3.jpg";
import trustConseilImg from "./Images/home/afrique-approche-conseil.png";
import trustExecutionImg from "./Images/home/afrique-execution-maitrisee.png";
import trustQualiteImg from "./Images/home/afrique-qualite-conformite.png";
import trustReactiviteImg from "./Images/home/afrique-reactivite.png";
import expertiseEquipeImg from "./Images/home/afrique-expertise-equipe.jpg";

const examSidebarItems = [
  { label: "Dashboard", icon: FaThLarge, active: false },
  { label: "Candidats", icon: FaUsers, active: true },
  { label: "Salles", icon: FaDoorOpen, active: false },
  { label: "Examens", icon: FaClipboardList, active: false },
  { label: "Résultats", icon: FaRegChartBar, active: false },
  { label: "Paramètres", icon: FaCogs, active: false },
];

const candidateRows = [
  { name: "Amina Yusuf", code: "00120" },
  { name: "Kwame Mensah", code: "00147" },
  { name: "Chidinma Okafor", code: "00189" },
  { name: "Tunde Adeyemi", code: "00203" },
  { name: "Fatima Ali", code: "00218" },
  { name: "Joseph Ndlovu", code: "00244" },
];

const roomRows = [
  { room: "Room 1", size: "32" },
  { room: "Room 2", size: "30" },
  { room: "Room 3", size: "28" },
  { room: "Room 4", size: "30" },
  { room: "Room 5", size: "26" },
  { room: "Room 6", size: "24" },
];

const seatingRows = [
  ["teal", "blue", "green", "teal", "green"],
  ["green", "teal", "blue", "green", "teal"],
  ["blue", "green", "teal", "blue", "green"],
  ["green", "blue", "green", "teal", "blue"],
];

const performerRows = [
  { name: "Amina Yusuf", medal: "gold" },
  { name: "Kwame Mensah", medal: "silver" },
  { name: "Chidinma Okafor", medal: "bronze" },
];

const heroServices = [
  { label: "Sites premium", icon: FaPalette },
  { label: "E-commerce", icon: FaRocket },
  { label: "Apps web", icon: FaCode },
  { label: "Réseaux IT", icon: FaServer },
];

const studioTimeline = [
  "Audit digital",
  "Design d'interface",
  "Développement",
  "Mise en ligne",
];

const heroTickerItems = [
  "Sites vitrines",
  "Boutiques e-commerce",
  "Interfaces UI/UX",
  "Applications web",
  "SEO technique",
  "Identité digitale",
  "Solutions scolaires",
  "Infrastructure IT",
];

function HomePage({ embedded = false }) {
  const heroSlides = useMemo(
    () => [
      image1,
      image2,
      image3,
      image4,
      "/home-cards/infrastructures-it.avif",
      "/home-cards/infrastructures-it-salle.avif",
    ],
    []
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useScrollReveal();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroSlides.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  const smartEduckaUrl = useMemo(() => {
    if (process.env.REACT_APP_SMART_EDUCKA_URL) {
      return process.env.REACT_APP_SMART_EDUCKA_URL;
    }

    const { hostname, protocol } = window.location;
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";

    if (isLocalHost) {
      return "http://127.0.0.1:8001";
    }

    const rootHost = hostname.startsWith("www.") ? hostname.slice(4) : hostname;
    return `${protocol}//school.${rootHost}`;
  }, []);

  const shopUrl = useMemo(() => {
    if (process.env.REACT_APP_SHOP_URL) {
      return process.env.REACT_APP_SHOP_URL;
    }

    const { hostname, protocol } = window.location;
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";

    if (isLocalHost) {
      return "http://127.0.0.1:3001";
    }

    const rootHost = hostname.startsWith("www.") ? hostname.slice(4) : hostname;
    return `${protocol}//shop.${rootHost}`;
  }, []);

  const trustPoints = [
    {
      title: "Approche conseil",
      image: trustConseilImg,
      text: "Nous auditons votre besoin, clarifions les priorités métier et transformons vos objectifs en feuille de route digitale lisible.",
    },
    {
      title: "Exécution maîtrisée",
      image: trustExecutionImg,
      text: "Chaque projet avance avec planning, maquettes, développement, tests, documentation et suivi pour livrer un résultat solide.",
    },
    {
      title: "Qualité et conformité",
      image: trustQualiteImg,
      text: "Nous soignons l'expérience utilisateur, la performance, la sécurité et la structure technique pour protéger votre image.",
    },
    {
      title: "Réactivité",
      image: trustReactiviteImg,
      text: "Nos équipes restent proches de vos besoins avec une communication rapide, claire et orientée solution.",
    },
    {
      title: "Fourniture IT et bureautique",
      image: "/home-cards/fourniture-it-bureautique.avif",
      text: "Nous accompagnons vos environnements de travail, de la sélection des produits à la livraison et la mise en service.",
      bullets: [
        "Ordinateurs de bureau, portables et stations de travail",
        "Imprimantes, scanners, onduleurs et accessoires",
        "Matériel réseau et périphériques",
        "Mobilier et équipements bureautiques",
      ],
    },
    {
      title: "Partenaire unique",
      image: "/home-cards/partenaire-unique.avif",
      text: "Du site web aux logiciels métier, de l'infrastructure au support, vous gardez un interlocuteur unique pour votre transformation numérique.",
    },
  ];

  const solutionsCards = [
    {
      image: "/home-cards/digitalisation-developpement.avif",
      title: "Sites web et plateformes",
      text: "Sites institutionnels, e-commerce, plateformes web et applications métier pour donner une vraie présence digitale à votre marque.",
      bullets: ["Sites web et portails", "Applications web", "Automatisation"],
      link: "/services",
      anchor: "#solutions",
      linkLabel: "Découvrir",
    },
    {
      image: "/home-cards/infrastructures-it.avif",
      title: "Infrastructure et équipements IT",
      text: "Conception, déploiement et sécurisation de votre environnement informatique et réseau.",
      bullets: ["Réseaux et sécurité", "Postes et imprimantes", "Sauvegarde et onduleurs"],
      link: "/services",
      anchor: "#solutions",
      linkLabel: "Explorer",
    },
    {
      image: "/home-cards/fourniture-it-bureautique.avif",
      title: "Fourniture IT et Bureautique",
      text: "Approvisionnement professionnel sur commande : matériels, consommables et fournitures de bureau.",
      bullets: ["Consommables", "Accessoires IT", "Fournitures bureau"],
      link: "/services",
      anchor: "#solutions",
      linkLabel: "Obtenir un devis",
    },
  ];

  const methodSteps = [
    {
      step: "01",
      title: "Audit et cadrage",
      text: "Compréhension du besoin, analyse, priorisation et périmètre.",
    },
    {
      step: "02",
      title: "Proposition et planification",
      text: "Solution technique, livrables, planning, budget et conditions.",
    },
    {
      step: "03",
      title: "Déploiement",
      text: "Mise en œuvre, tests, sécurisation, documentation et recette.",
    },
    {
      step: "04",
      title: "Accompagnement",
      text: "Formation, support, maintenance et amélioration continue.",
    },
  ];

  const commitments = [
    "Confidentialité et sécurité des données",
    "Qualité d'exécution et respect des délais",
    "Transparence : devis détaillé et livrables clairs",
    "Support : suivi après déploiement",
  ];

  const expertiseCards = [
    {
      image: expertiseEquipeImg,
      title: "Équipe professionnelle",
      text: "Chez SAMOL TECHNOLOGIES, nos équipes techniques et fonctionnelles travaillent avec vos responsables métier pour livrer des solutions utiles, bien documentées et faciles à exploiter au quotidien.",
    },
    {
      image: "/home-cards/qualite-innovation.avif",
      title: "Qualité et innovation",
      text: "Nous privilégions des choix techniques stables, sécurisés et évolutifs, puis nous intégrons l'innovation là où elle apporte un gain réel: rapidité, fiabilité et meilleure expérience utilisateur.",
    },
    {
      image: "/home-cards/sites-web-ecommerce.avif",
      title: "Sites web et e-commerce",
      text: "Nous concevons des sites vitrines et des plateformes e-commerce avec des parcours lisibles, des contenus bien structurés et une base technique prête pour votre croissance.",
    },
    {
      image: "/home-cards/logiciels-metier.avif",
      title: "Logiciels métier",
      text: "Nous développons des logiciels métier sur mesure pour les écoles et les PME afin d'automatiser les tâches critiques, fiabiliser les données et mieux piloter l'activité.",
    },
  ];
  const NavAction = ({ to, anchor, className, children }) =>
    embedded ? (
      <a href={anchor} className={className}>
        {children}
      </a>
    ) : (
      <Link to={to} className={className}>
        {children}
      </Link>
    );

  return (
    <div className="page-shell">
      {!embedded && <Header />}

      <main className="home-main">
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-copy">
              <div className="hero-kicker">
                <FaBolt aria-hidden="true" />
                Studio web, logiciels et infrastructure IT
              </div>
              <h1 className="hero-signature hero-welcome">
                <span>BIENVENUE CHEZ SAMOL TECHNOLOGIES</span>
                <strong>Votre partenaire stratégique en transformation numérique.</strong>
              </h1>
              <p className="hero-subtitle">
                Nous concevons des présences digitales solides, élégantes et
                faciles à exploiter: stratégie, design, développement, mise en
                ligne et accompagnement technique.
              </p>

              <div className="hero-text-marquee" aria-label="Expertises SATECH">
                <div className="hero-text-track">
                  {[...heroTickerItems, ...heroTickerItems].map((item, index) => (
                    <span key={`${item}-${index}`}>{item}</span>
                  ))}
                </div>
              </div>

              <div className="hero-actions">
                <NavAction to="/contact" anchor="#contact" className="btn btn-primary">
                  <span>Demander un audit</span>
                  <FaArrowRight aria-hidden="true" />
                </NavAction>

                <NavAction to="/nos-realisations" anchor="#nos-realisations" className="btn btn-secondary">
                  <span>Nos réalisations</span>
                </NavAction>
              </div>

              <div className="hero-service-strip" aria-label="Services principaux">
                {heroServices.map((service) => {
                  const Icon = service.icon;
                  return (
                    <span className="hero-service-pill" key={service.label}>
                      <Icon aria-hidden="true" />
                      {service.label}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="hero-visual" aria-label="Aperçu de production digitale SATECH">
              <div className="hero-media-frame">
                {heroSlides.map((image, index) => (
                  <div
                    className={`hero-slide ${
                      index === currentImageIndex ? "active" : ""
                    }`}
                    key={image}
                  >
                    <img src={image} alt={`Présentation Samol ${index + 1}`} loading="lazy" />
                  </div>
                ))}
                <div className="hero-overlay"></div>
                <div className="hero-dots">
                  {heroSlides.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={index === currentImageIndex ? "dot active" : "dot"}
                      onClick={() => setCurrentImageIndex(index)}
                      aria-label={`Afficher le visuel ${index + 1}`}
                    ></button>
                  ))}
                </div>

              </div>

              <div className="hero-image-marquee" aria-hidden="true">
                <div className="hero-image-track">
                  {[...heroSlides, ...heroSlides].map((image, index) => (
                    <img src={image} alt="" key={`${image}-${index}`} loading="lazy" />
                  ))}
                </div>
              </div>

              <div className="studio-window studio-window-main">
                <div className="studio-window-bar">
                  <span></span>
                  <span></span>
                  <span></span>
                  <strong>satech.studio</strong>
                </div>
                <div className="studio-screen">
                  <div className="studio-preview">
                    <span className="studio-badge">Landing page</span>
                    <h3>Site premium prêt à convertir</h3>
                    <div className="studio-preview-lines">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <div className="studio-side">
                    <div className="studio-score">
                      <span>UX Score</span>
                      <strong>96%</strong>
                    </div>
                    <div className="studio-bars" aria-hidden="true">
                      <span style={{ "--bar": "72%" }}></span>
                      <span style={{ "--bar": "88%" }}></span>
                      <span style={{ "--bar": "64%" }}></span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="studio-window studio-window-floating">
                <div className="floating-title">
                  <FaLaptopCode aria-hidden="true" />
                  Stack projet
                </div>
                <div className="stack-grid">
                  <span>Design</span>
                  <span>React</span>
                  <span>API</span>
                  <span>SEO</span>
                </div>
              </div>

              <div className="studio-window studio-window-timeline">
                {studioTimeline.map((item) => (
                  <div className="timeline-item" key={item}>
                    <FaCheckCircle aria-hidden="true" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <section className="intro-section">
          <div className="intro-card reveal-on-scroll" data-reveal>
            <h2>Une entreprise innovante en solutions informatiques</h2>
            <p>
              SAMOL TECHNOLOGIES est spécialisée dans le développement de
              logiciels, de sites web, d&apos;applications mobiles et de
              solutions de gestion informatique. Nous proposons également
              l&apos;installation et la configuration de réseaux
              d&apos;entreprises ainsi que des services IT et fournitures de bureau adaptés à la
              croissance de nos clients.
            </p>
          </div>
          <div className="intro-card reveal-on-scroll" data-reveal>
            <h3>Nos piliers historiques</h3>
            <ul>
              <li>Développement de logiciels de gestion</li>
              <li>Conception de sites web et e-commerce</li>
              <li>Création d&apos;applications mobiles et web</li>
              <li>Installation et sécurisation de réseaux d&apos;entreprise</li>
              <li>Fourniture de matériel informatique et bureautique</li>
            </ul>
          </div>
        </section>
        <section className="trust-section">
          <div className="section-title">
            <h2>Pourquoi choisir SATECH</h2>
            <p>
              Une approche premium centrée sur la qualité, la sécurité et la
              performance.
            </p>
          </div>

          <div className="trust-grid trust-grid-top">
            {trustPoints.slice(0, 3).map((p) => (
              <article className="trust-card reveal-on-scroll" data-reveal key={p.title}>
                <img src={p.image} alt={p.title} loading="lazy" />
                <h3>{p.title}</h3>
                <p>{p.text}</p>
                {p.bullets && (
                  <ul className="trust-card-list">
                    {p.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>

          <div className="trust-grid trust-grid-bottom">
            {trustPoints.slice(3).map((p) => (
              <article className="trust-card reveal-on-scroll" data-reveal key={p.title}>
                <img src={p.image} alt={p.title} loading="lazy" />
                <h3>{p.title}</h3>
                <p>{p.text}</p>
                {p.bullets && (
                  <ul className="trust-card-list">
                    {p.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </section>

        <section className="expertise-section">
          <div className="section-title">
            <h2>Notre expertise terrain</h2>
            <p>Trouvez rapidement la solution adaptée à vos besoins.</p>
          </div>
          <div className="expertise-grid">
            {expertiseCards.map((card) => (
              <article className="expertise-card reveal-on-scroll" data-reveal key={card.title}>
                <img src={card.image} alt={card.title} />
                <div className="expertise-text">
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="home-solutions-section">
          <div className="section-title">
            <h2>Nos solutions</h2>
            <p>
              Un accompagnement complet : conseil, mise en œuvre, support et
              amélioration continue.
            </p>
          </div>

          <div className="home-solutions-grid">
            {solutionsCards.map((card) => (
              <article className="home-solution-card reveal-on-scroll" data-reveal key={card.title}>
                <img src={card.image} alt={card.title} loading="lazy" />
                <div className="home-solution-content">
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>

                  <ul className="home-solution-bullets">
                    {card.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>

                  <div className="solution-actions">
                    <NavAction to={card.link} anchor={card.anchor}>
                      {card.linkLabel}
                    </NavAction>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="section-cta">
            <NavAction to="/contact" anchor="#contact" className="btn btn-primary">
              Obtenir un devis
            </NavAction>
            <NavAction to="/services" anchor="#solutions" className="btn btn-secondary">
              Voir toutes les solutions
            </NavAction>
            <a
              href={shopUrl}
              className="btn btn-secondary"
              target="_blank"
              rel="noreferrer"
            >
              Boutique IT
            </a>
          </div>
        </section>

        <section className="product-section">
          <div className="product-inner reveal-on-scroll" data-reveal>
            <div className="product-copy">
              <p className="section-kicker">Solution dédiée écoles</p>
              <h2>
                Smart-Educka : plateforme de gestion et de digitalisation des
                établissements
              </h2>
              <p>
                Smart-Educka modernise l&apos;administration scolaire : gestion
                des élèves, suivi des paiements, notes, bulletins, statistiques
                et communication. Une solution conçue pour améliorer la
                traçabilité, réduire les tâches manuelles et renforcer la
                performance opérationnelle.
              </p>

              <div className="product-benefits">
                <div className="benefit">
                  <h4>Pilotage en temps réel</h4>
                  <p>Indicateurs, états et suivis pour une meilleure décision.</p>
                </div>
                <div className="benefit">
                  <h4>Traçabilité et contrôle</h4>
                  <p>Historique, reporting et données structurées.</p>
                </div>
                <div className="benefit">
                  <h4>Examen blanc</h4>
                  <p>Affectation, plan de salle et résultats sur une interface unifiée.</p>
                </div>
              </div>

              <div className="hero-actions">
                <NavAction to="/contact" anchor="#contact" className="btn btn-primary">
                  Demander une démonstration
                </NavAction>
                <a
                  href={smartEduckaUrl}
                  className="btn btn-secondary"
                  target="_blank"
                  rel="noreferrer"
                >
                  Connexion Smart-Educka
                </a>
              </div>
            </div>

            <div className="product-visual">
              <div className="mock-exam-showcase" aria-label="Aperçu du module examen blanc Smart-Educka">
                <article className="mock-device mock-device-main">
                  <div className="mock-device-bar">
                    <span className="mock-brand-mark" aria-hidden="true" />
                    <span className="mock-device-title">smart-educka</span>
                    <div className="mock-device-actions" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>

                  <div className="mock-device-body">
                    <aside className="mock-sidebar">
                      <div className="mock-sidebar-logo">
                        <span className="mock-brand-mark" aria-hidden="true" />
                        <strong>smart-educka</strong>
                      </div>

                      <nav className="mock-sidebar-nav" aria-label="Menu Smart-Educka">
                        {examSidebarItems.map((item) => {
                          const Icon = item.icon;
                          return (
                            <div
                              className={`mock-sidebar-item${item.active ? " is-active" : ""}`}
                              key={item.label}
                            >
                              <Icon aria-hidden="true" />
                              <span>{item.label}</span>
                            </div>
                          );
                        })}
                      </nav>
                    </aside>

                    <div className="mock-workspace">
                      <div className="mock-workspace-header">
                        <div>
                          <span className="mock-eyebrow">Module</span>
                          <h3>Mock Exam</h3>
                        </div>
                        <span className="mock-status-pill">Examen en préparation</span>
                      </div>

                      <div className="mock-exam-panels">
                        <section className="mock-panel">
                          <div className="mock-panel-heading">
                            <h4>Candidate List</h4>
                            <span>248 candidats</span>
                          </div>
                          <div className="mock-list">
                            {candidateRows.map((candidate) => (
                              <div className="mock-list-row" key={candidate.code}>
                                <div className="mock-candidate">
                                  <span className="mock-avatar" aria-hidden="true" />
                                  <span>{candidate.name}</span>
                                </div>
                                <span>{candidate.code}</span>
                              </div>
                            ))}
                          </div>
                        </section>

                        <section className="mock-panel">
                          <div className="mock-panel-heading">
                            <h4>Room Assignment</h4>
                            <span>6 salles</span>
                          </div>
                          <div className="mock-list">
                            {roomRows.map((room) => (
                              <div className="mock-list-row" key={room.room}>
                                <span>{room.room}</span>
                                <strong>{room.size}</strong>
                              </div>
                            ))}
                          </div>
                        </section>

                        <section className="mock-panel">
                          <div className="mock-panel-heading">
                            <h4>Seating Plan - Room 1</h4>
                            <span>32 places</span>
                          </div>
                          <div className="mock-seating-grid" aria-hidden="true">
                            {seatingRows.flatMap((row, rowIndex) =>
                              row.map((seatColor, seatIndex) => (
                                <span
                                  className={`mock-seat seat-${seatColor}`}
                                  key={`${rowIndex}-${seatIndex}`}
                                />
                              ))
                            )}
                          </div>
                        </section>
                      </div>
                    </div>
                  </div>
                </article>

                <article className="mock-device mock-device-secondary">
                  <div className="mock-device-bar">
                    <span className="mock-brand-mark" aria-hidden="true" />
                    <span className="mock-device-title">Mock Exam Results</span>
                    <div className="mock-device-actions" aria-hidden="true">
                      <span />
                      <span />
                    </div>
                  </div>

                  <div className="mock-results-body">
                    <section className="mock-results-card mock-results-rate">
                      <div className="mock-panel-heading">
                        <h4>Pass Rate</h4>
                        <span>Global</span>
                      </div>
                      <div className="mock-rate-value">82%</div>
                      <div className="mock-line-chart" aria-hidden="true">
                        <span className="line-segment segment-a" />
                        <span className="line-segment segment-b" />
                        <span className="line-segment segment-c" />
                        <span className="line-segment segment-d" />
                      </div>
                    </section>

                    <section className="mock-results-card">
                      <div className="mock-panel-heading">
                        <h4>Grade Distribution</h4>
                        <span>248 copies</span>
                      </div>
                      <div className="mock-donut-wrap">
                        <div className="mock-donut" aria-hidden="true" />
                      </div>
                    </section>

                    <section className="mock-results-card mock-results-list">
                      <div className="mock-panel-heading">
                        <h4>Top Performers</h4>
                        <span>Mentions</span>
                      </div>
                      {performerRows.map((performer, index) => (
                        <div className="mock-performer-row" key={performer.name}>
                          <span>{index + 1}. {performer.name}</span>
                          <FaMedal className={`medal-${performer.medal}`} aria-hidden="true" />
                        </div>
                      ))}
                    </section>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="method-section">
          <div className="section-title">
            <h2>Notre méthode d&apos;intervention</h2>
            <p>Un processus structuré, clair et orienté résultats.</p>
          </div>

          <div className="method-grid">
            {methodSteps.map((s) => (
              <article className="method-card reveal-on-scroll" data-reveal key={s.step}>
                <div className="method-step">{s.step}</div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="sectors-section">
          <div className="section-title">
            <h2>Solutions par secteur</h2>
            <p>Des offres adaptées aux exigences des écoles et des entreprises.</p>
          </div>

          <div className="sectors-grid">
            <article className="sector-card reveal-on-scroll" data-reveal>
              <h3>Écoles et centres de formation</h3>
              <p>
                Digitalisation, infrastructure scolaire, déploiement
                Smart-Educka, formation et support.
              </p>
              <NavAction to="/solutions-ecoles" anchor="#solutions-ecoles">
                Voir les solutions écoles
              </NavAction>
            </article>

            <article className="sector-card reveal-on-scroll" data-reveal>
              <h3>PME et organisations</h3>
              <p>
                Infrastructure IT, sécurité, sites web, applications internes,
                maintenance et approvisionnement.
              </p>
              <NavAction to="/solutions-pme" anchor="#solutions-pme">
                Voir les solutions PME
              </NavAction>
            </article>
          </div>
        </section>

        <section className="commitments-section">
          <div className="section-title">
            <h2>Nos engagements</h2>
            <p>
              Une relation de confiance fondée sur la qualité et la
              transparence.
            </p>
          </div>

          <div className="commitments-card reveal-on-scroll" data-reveal>
            <ul>
              {commitments.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="final-cta-section">
          <div className="final-cta-card reveal-on-scroll" data-reveal>
            <h2>Prêt à moderniser votre organisation ?</h2>
            <p>
              Planifions un premier échange. Nous vous proposons un diagnostic
              et une feuille de route adaptée à vos objectifs, vos contraintes
              et votre budget.
            </p>
            <div className="hero-actions">
              <NavAction to="/contact" anchor="#contact" className="btn btn-primary">
                Demander un audit
              </NavAction>
              <NavAction to="/services" anchor="#solutions" className="btn btn-secondary">
                Découvrir nos solutions
              </NavAction>
            </div>
          </div>
        </section>
      </main>

      {!embedded && (
        <df-messenger
          intent="WELCOME"
          chat-title="SoniaAssistanteWeb"
          agent-id="926c948e-9249-4a23-86a2-ab08e594f84a"
          language-code="fr"
        ></df-messenger>
      )}

      {!embedded && <Footer />}
    </div>
  );
}

export default HomePage;






